import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'react-query'

import {
  Box,
  Button,
  Dialog,
  Icon,
  Heading,
  HiddenInput,
  LoadingPlane,
  Text,
} from '@redriverpress/system'

import BackToBack from 'Images/flashcards/print/flashcard-styles_back-to-back.png'
import ImageAndText from 'Images/flashcards/print/flashcard-styles_image-and-text.png'
import ImageOnly from 'Images/flashcards/print/flashcard-styles_image-only.png'
import TextOnly from 'Images/flashcards/print/flashcard-styles_text-only.png'
import OpenSans from 'Images/flashcards/print/fonts_open-sans.png'
import Quicksand from 'Images/flashcards/print/fonts_quicksand.png'
import Color from 'Images/flashcards/print/color-settings_color.png'
import BlackAndWhite from 'Images/flashcards/print/color-settings_black-and-white.png'
import TwoPerPage from 'Images/flashcards/print/images-per-page_2.png'
import FourPerPage from 'Images/flashcards/print/images-per-page_4.png'
import EightPerPage from 'Images/flashcards/print/images-per-page_8.png'
import SixteenPerPage from 'Images/flashcards/print/images-per-page_16.png'

import StyledDialogContent from '../StyledDialogContent'
import {
  postFlashcardPdf,
  getFlashcardPdf,
} from '../services/flashcardPrintServices'

const PrintOptionsGrid = styled(Box).attrs(props => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, 140px)',
  gridGap: [props.theme.space[3], null, props.theme.space[4]],
  mb: 5,
}))``

const PrintOption = styled(Text).attrs({
  as: 'label',
  display: 'block',
  fontWeight: 600,
})`
  cursor: pointer;
`

const PrintOptionImage = styled(Box).attrs({
  backgroundColor: 'white',
  border: 'none',
  borderRadius: 'rounded',
  size: '140px',
  mb: 2,
})`
  background-image: url('${props => props.img}');
  background-size: contain;
  background-position: center;

  input:checked + & {
    box-shadow: 0 0 0 2px ${props => props.theme.colors.blue500};
  }

  input:focus + & {
    box-shadow: 0 0 0 4px ${props => props.theme.colors.blue500};
  }
`

const layouts = {
  imageOnly: 'only_image',
  textOnly: 'only_text',
  backToBack: 'back_to_back',
  imageAndText: 'image_and_text',
}

const fonts = {
  openSans: 'opensans',
  quicksand: 'quicksand',
}

export default function PrintFlashcardsDialog({
  flashcards,
  onOpenChange,
  open,
  title,
}) {
  const [layout, setLayout] = useState(layouts.imageOnly)
  const [font, setFont] = useState(fonts.openSans)
  const [color, setColor] = useState(true)
  const [perPage, setPerPage] = useState(1)

  const [pdfId, setPdfId] = useState(null)
  const [pdfUrl, setPdfUrl] = useState(null)

  const printMutation = useMutation(
    () =>
      postFlashcardPdf({
        flashcards: flashcards.map(
          ({ id, image, imageGrayscale, userTitle }) => ({
            id,
            image,
            imageGrayscale,
            title: userTitle,
          })
        ),
        layout,
        font,
        color,
        perPage,
      }),
    {
      onSuccess: ({ data: id }) => setPdfId(id),
    }
  )

  const { isLoading: pdfIsGenerating } = useQuery(
    ['flashcardPdfUrl', pdfId],
    () => getFlashcardPdf(pdfId),
    {
      enabled: pdfId !== null,
      retry: true,
      retryDelay: 1000,
      onSuccess: ({ data: url }) => {
        setPdfId(null) // clear pdfId for next print job, if any
        setPdfUrl(url)
      },
    }
  )

  useEffect(() => {
    pdfUrl && window.open(pdfUrl, '_blank')
  }, [pdfUrl])

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <StyledDialogContent>
          <Box bg='white' p={3} pb={4}>
            <Dialog.Close
              position='absolute'
              top={[1, null, 2]}
              right={[1, null, 2]}
            />
            <Heading as='h3' mt={4} mb={2}>
              Print {title ? `“${title}”` : 'Unsaved Selection'}
            </Heading>
            <Text color='gray700'>
              {flashcards.length} flashcard{flashcards.length !== 1 && 's'}
            </Text>
          </Box>

          <Box py={4} px={3} bg='gray100'>
            <Heading as='h4' mb={4}>
              Flashcard Styles
            </Heading>
            <PrintOptionsGrid>
              <PrintOption>
                <HiddenInput
                  type='radio'
                  name='layout'
                  value={layouts.imageOnly}
                  checked={layout === layouts.imageOnly}
                  onChange={() => setLayout(layouts.imageOnly)}
                />
                <PrintOptionImage img={ImageOnly} />
                <Text>Image only</Text>
              </PrintOption>
              <PrintOption>
                <HiddenInput
                  type='radio'
                  name='style'
                  value={layouts.imageAndText}
                  checked={layout === layouts.imageAndText}
                  onChange={() => setLayout(layouts.imageAndText)}
                />
                <PrintOptionImage img={ImageAndText} />
                <Text>Image & text</Text>
              </PrintOption>
              <PrintOption>
                <HiddenInput
                  type='radio'
                  name='style'
                  value={layouts.backToBack}
                  checked={layout === layouts.backToBack}
                  onChange={() => setLayout(layouts.backToBack)}
                />
                <PrintOptionImage img={BackToBack} />
                <Text>
                  Image & text
                  <br /> (back-to-back)
                </Text>
              </PrintOption>
              <PrintOption>
                <HiddenInput
                  type='radio'
                  name='style'
                  value={layouts.textOnly}
                  checked={layout === layouts.textOnly}
                  onChange={() => setLayout(layouts.textOnly)}
                />
                <PrintOptionImage img={TextOnly} />
                <Text>Text only</Text>
              </PrintOption>
            </PrintOptionsGrid>

            <Heading as='h4' mb={4}>
              Fonts
            </Heading>
            <PrintOptionsGrid>
              <PrintOption>
                <HiddenInput
                  type='radio'
                  name='font'
                  value={fonts.openSans}
                  checked={font === fonts.openSans}
                  onChange={() => setFont(fonts.openSans)}
                />
                <PrintOptionImage img={OpenSans} />
              </PrintOption>
              <PrintOption>
                <HiddenInput
                  type='radio'
                  name='font'
                  value={fonts.quicksand}
                  checked={font === fonts.quicksand}
                  onChange={() => setFont(fonts.quicksand)}
                />
                <PrintOptionImage img={Quicksand} />
              </PrintOption>
            </PrintOptionsGrid>

            <Heading as='h4' mb={4}>
              Color Settings
            </Heading>
            <PrintOptionsGrid>
              <PrintOption>
                <HiddenInput
                  type='radio'
                  name='color'
                  value='color'
                  checked={color}
                  onChange={() => setColor(true)}
                />
                <PrintOptionImage img={Color} />
                <Text>Color</Text>
              </PrintOption>
              <PrintOption>
                <HiddenInput
                  type='radio'
                  name='color'
                  value='black-and-white'
                  checked={!color}
                  onChange={() => setColor(false)}
                />
                <PrintOptionImage img={BlackAndWhite} />
                <Text>Black & white</Text>
              </PrintOption>
            </PrintOptionsGrid>

            <Heading as='h4' mb={4}>
              Images per Page
            </Heading>
            <PrintOptionsGrid>
              <PrintOption>
                <HiddenInput
                  type='radio'
                  name='perPage'
                  value='1'
                  checked={perPage === 1}
                  onChange={() => setPerPage(1)}
                />
                <PrintOptionImage />
                <Text>1</Text>
              </PrintOption>
              <PrintOption>
                <HiddenInput
                  type='radio'
                  name='perPage'
                  value='2'
                  checked={perPage === 2}
                  onChange={() => setPerPage(2)}
                />
                <PrintOptionImage img={TwoPerPage} />
                <Text>2</Text>
              </PrintOption>
              <PrintOption>
                <HiddenInput
                  type='radio'
                  name='perPage'
                  value='4'
                  checked={perPage === 4}
                  onChange={() => setPerPage(4)}
                />
                <PrintOptionImage img={FourPerPage} />
                <Text>4</Text>
              </PrintOption>
              <PrintOption>
                <HiddenInput
                  type='radio'
                  name='perPage'
                  value='8'
                  checked={perPage === 8}
                  onChange={() => setPerPage(8)}
                />
                <PrintOptionImage img={EightPerPage} />
                <Text>8</Text>
              </PrintOption>
              <PrintOption>
                <HiddenInput
                  type='radio'
                  name='perPage'
                  value='16'
                  checked={perPage === 16}
                  onChange={() => setPerPage(16)}
                />
                <PrintOptionImage img={SixteenPerPage} />
                <Text>16</Text>
              </PrintOption>
            </PrintOptionsGrid>

            <Box pb={4} textAlign='center'>
              <Button
                onClick={printMutation.mutate}
                disabled={printMutation.isLoading || pdfIsGenerating}
              >
                <Icon icon='print' pr={2} />
                Print Flashcards
              </Button>
            </Box>
          </Box>
          <Dialog.Root open={printMutation.isLoading || pdfIsGenerating}>
            <Dialog.Portal>
              <Dialog.Overlay zIndex={7} />
              <Dialog.Content
                asChild
                onEscapeKeyDown={e => e.preventDefault()}
                onInteractOutside={e => e.preventDefault()}
                onPointerDownOutside={e => e.preventDefault()}
              >
                <Box
                  position='fixed'
                  top={0}
                  bottom={0}
                  left={0}
                  right={0}
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                  zIndex={7}
                >
                  <Box
                    bg='white'
                    p='1.5rem' // designer request
                    pb={4}
                    maxWidth='90vw'
                    borderRadius='rounded'
                    textAlign='center'
                  >
                    <LoadingPlane />
                    <Text measure mx='auto' mt={4}>
                      Please wait while we generate your printable flashcard
                      file. This may take&nbsp;a&nbsp;minute.
                    </Text>
                    <Text measure mx='auto' fontSize={0}>
                      Your PDF will open in a new browser tab when
                      it’s&nbsp;ready.
                    </Text>
                  </Box>
                </Box>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </StyledDialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

PrintFlashcardsDialog.defaultProps = {
  title: null,
}

PrintFlashcardsDialog.propTypes = {
  flashcards: PropTypes.arrayOf(PropTypes.object).isRequired,
  onOpenChange: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  title: PropTypes.string,
}
